// ── PDF extraction facade ─────────────────────────────────────────────────────
// This file is a thin barrel over the modular PDF extraction pipeline in
// ./pdf/*, kept so existing imports (RRDCombinedInventoryModal.tsx,
// RRDAzureOpenAIUtils.ts) don't need to change. The implementation is split
// into focused modules:
//
//   pdf/RRDPdfConfig.ts             - env-driven feature flags (regex/OCR/Azure AI)
//   pdf/RRDPdfCore.ts               - file reading, validation, page count, base64 images
//   pdf/RRDPdfOcrService.ts         - shared/reused Tesseract OCR worker
//   pdf/RRDPdfTextExtractor.ts      - single-pass PDF text extraction (text layer + OCR fallback)
//   pdf/RRDPdfFieldParser.ts        - regex-based field extraction
//   pdf/RRDPdfDocumentClassifier.ts - document type classification
//
// See pdf/RRDPdfConfig.ts for the regex-vs-OCR tradeoff notes and the new
// REACT_APP_RRD_USE_OCR_EXTRACTION / REACT_APP_RRD_USE_REGEX_EXTRACTION flags.

import { PDF_EXTRACTION_PAGE_COUNT } from './pdf/RRDPdfConfig';
import { extractPdfPageTexts } from './pdf/RRDPdfTextExtractor';
import { classifyPageTexts, DocumentTypeClassification } from './pdf/RRDPdfDocumentClassifier';
import { extractFieldsFromPageTexts, ExtractedFieldsWithQuality } from './pdf/RRDPdfFieldParser';

export {
  PDF_EXTRACTION_PAGE_COUNT,
  AZURE_AI_CLASSIFICATION_ENABLED,
  OCR_EXTRACTION_ENABLED,
  REGEX_EXTRACTION_ENABLED,
} from './pdf/RRDPdfConfig';

export type { PdfImageUrlEntry } from './pdf/RRDPdfCore';
export {
  readFileAsArrayBuffer,
  validatePDFFile,
  getPDFPageCount,
  convertPDFPagesToBase64Json,
} from './pdf/RRDPdfCore';

export { terminateOcrWorker } from './pdf/RRDPdfOcrService';

export type { PageText } from './pdf/RRDPdfTextExtractor';
export { extractPdfPageTexts } from './pdf/RRDPdfTextExtractor';

export type {
  ExtractedFields,
  ExtractedFieldScores,
  ExtractedFieldsWithQuality,
} from './pdf/RRDPdfFieldParser';
export {
  AUTOFILL_QUALITY_THRESHOLD,
  extractFieldsFromPageTexts,
  extractFieldsFromPDFPageWithQuality,
} from './pdf/RRDPdfFieldParser';

export type {
  ClassifiedDocumentType,
  DocumentTypeClassification,
} from './pdf/RRDPdfDocumentClassifier';
export {
  classifyDocumentText,
  classifyPageTexts,
  classifyPDFDocumentType,
} from './pdf/RRDPdfDocumentClassifier';

export type PdfDocumentAnalysis = {
  classification: DocumentTypeClassification;
  extraction: ExtractedFieldsWithQuality;
};

/**
 * Runs document classification and field extraction together from a single
 * PDF parse. Prefer this over calling `classifyPDFDocumentType` and
 * `extractFieldsFromPDFPageWithQuality` back-to-back on the same file — doing
 * so opens/parses the PDF twice and, for scanned documents, runs OCR twice.
 */
export async function analyzePdfDocument(
  file: File,
  maxPages: number = PDF_EXTRACTION_PAGE_COUNT
): Promise<PdfDocumentAnalysis> {
  const pageTexts = await extractPdfPageTexts(file, maxPages);
  const classification = classifyPageTexts(pageTexts.map((p) => p.fullText));
  const extraction = extractFieldsFromPageTexts(pageTexts);
  return { classification, extraction };
}

